import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Box,
  Chip,
  useTheme,
  Paper,
} from '@mui/material';
import {
  ShoppingCart,
  Star,
  ArrowForward,
  Palette,
  Image,
  TextFields,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { productService } from '../services/api';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Alert, Stack, IconButton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import RefreshIcon from '@mui/icons-material/Refresh';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { toast } from 'react-toastify';
import { COLORS, withOpacity } from '../theme/colors';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [allFeaturedProducts, setAllFeaturedProducts] = useState([]); // Store all featured products
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLabels, setSelectedLabels] = useState([]); // Active label filters
  const [availableLabels, setAvailableLabels] = useState([]); // All unique labels from products
  const navigate = useNavigate();
  const theme = useTheme();
  const { addToCart } = useCart();
  const { currentUser, sendVerificationEmailNow, refreshEmailVerificationStatus, sendingVerification } = useAuth();
  const [verificationJustSent, setVerificationJustSent] = useState(false);

  const handleSendVerification = async () => {
    try {
      const sent = await sendVerificationEmailNow();
      if (sent) {
        setVerificationJustSent(true);
        toast.success('Verification email sent');
      }
    } catch (e) {
      toast.error('Failed to send verification email');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [featuredProductsResponse, labelsResponse, categoriesResponse] = await Promise.all([
        productService.getFeaturedProducts(),
        productService.getProductLabels(),
        productService.getCategories()
      ]);
      
      //console.log('🌟 Featured Products Response:', featuredProductsResponse);
      //console.log('🏷️ Labels Response:', labelsResponse);
      //console.log('📂 Categories Response:', categoriesResponse);
      
      // Handle featured products
      if (featuredProductsResponse.data && featuredProductsResponse.data.products) {
        const featuredProducts = featuredProductsResponse.data.products;
        //console.log('🌟 Featured products loaded:', featuredProducts.length);
        
        setAllFeaturedProducts(featuredProducts);
        setFeaturedProducts(featuredProducts);
      }
      
      // Handle labels
      if (labelsResponse.data && labelsResponse.data.labels) {
        const labels = labelsResponse.data.labels;
        //console.log('🏷️ Labels loaded:', labels);
        //console.log('🏷️ Label stats:', labelsResponse.data.stats);
        
        setAvailableLabels(labels);
      }
      
      // Handle categories
      if (categoriesResponse.data && categoriesResponse.data.categories) {
        const parentCategories = categoriesResponse.data.categories.filter(cat => cat.level === 0);
        setCategories(parentCategories);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load homepage data');
    } finally {
      setLoading(false);
    }
  };

  // Filtering helpers
  const filterProductsByLabels = (products, selected) => {
    if (selected.length === 0) return products;
    return products.filter(product => {
      const label = product.label && product.label.trim();
      if (label && selected.includes(label)) return true;
      if (selected.includes('No Label') && !label) return true;
      return false;
    });
  };

  const handleLabelToggle = (label) => {
    setSelectedLabels(prev => {
      const updated = prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label];
      const filtered = filterProductsByLabels(allFeaturedProducts, updated);
      setFeaturedProducts(filtered); // No limit
      return updated;
    });
  };

  const clearAllFilters = () => {
    setSelectedLabels([]);
    setFeaturedProducts(allFeaturedProducts); // No limit
  };

  const handleAddToCart = async (product) => {
    const requiresCustomization = product.imageRequired > 0 || product.textRequired;
    if (requiresCustomization) {
      navigate(`/products/${product.docId || product.id}`);
      return;
    }
    if (!currentUser) {
      sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
      return;
    }
    try {
      await addToCart(product.docId || product.id, 1);
      toast.success('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
        navigate('/login');
      } else {
        toast.error('Failed to add product to cart');
      }
    }
  };

  // Debug: Log current state before render
  //console.log('Rendering with featuredProducts.length:', featuredProducts.length);
  //console.log('Current selectedLabels:', selectedLabels);
  //console.log('Available labels for buttons:', availableLabels);

  return (
    <>
      <Helmet>
        <title>The Random Store - Custom Photo Frames, Lamps & Personalized Gifts | Online India</title>
        <meta name="description" content="Shop custom photo frames, personalized lamps, lithophane lamps, custom keychains, and unique home decor at The Random Store. Custom products with fast delivery across India." />
        <meta name="keywords" content="custom photo frame, custom lamps, custom photo frames india, personalized photo frames, lithophane lamps, custom name keychains, personalized keychains, custom home decor, unique gifts india, custom printing services" />
        <meta property="og:title" content="The Random Store - Custom Photo Frames, Lamps & Personalized Gifts" />
        <meta property="og:description" content="Shop custom photo frames, personalized lamps, lithophane lamps, custom keychains, and unique home decor at The Random Store. Custom products with fast delivery across India." />
        <meta property="og:image" content="/images/The-Random-Store---Logo---Transparent%20-%20Copy.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://therandomstores.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Random Store - Curated Randomness, Custom Products, and More" />
        <meta name="twitter:description" content="Discover curated randomness at The Random Store. Shop unique, custom, and modern products across categories with a seamless experience." />
        <meta name="twitter:image" content="/images/The-Random-Store---Logo---Transparent%20-%20Copy.png" />
      </Helmet>

      {/* Info Bar for Current Offers */}
      <Box sx={{
        width: '100%',
        bgcolor: COLORS.primary.main,
        color: 'white',
        py: 1,
        textAlign: 'center',
        fontWeight: 500,
        fontSize: { xs: '0.95rem', sm: '1.05rem' },
        letterSpacing: 0.5,
        zIndex: -1,
        top: 0,
        boxShadow: '0 2px 8px 0 rgba(0,0,0,0.04)',
      }}>
        🎉 Free Shipping on all orders! 
      </Box>

      <Box>
      {/* Email Verification Banner */}
      {currentUser && !currentUser.emailVerified && (
        <Box sx={{ bgcolor: withOpacity(COLORS.warning?.main || '#f59e0b',0.08), borderBottom:`1px solid ${withOpacity(COLORS.warning?.main || '#f59e0b',0.3)}`, py:1.5, mb:0 }}>
          <Container maxWidth="lg">
            <Stack direction={{ xs:'column', sm:'row' }} spacing={1.5} alignItems={{ xs:'flex-start', sm:'center' }} justifyContent="space-between">
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight:600, display:'flex', alignItems:'center', gap:1 }}>
                  Verify your email to secure your account {verificationJustSent && <CheckCircleIcon color="success" fontSize="small" />}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  A verified email helps with order updates & password recovery.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EmailIcon fontSize="small" />}
                  disabled={sendingVerification}
                  onClick={handleSendVerification}
                >
                  {sendingVerification? 'Sending...' : verificationJustSent? 'Resend' : 'Send Link'}
                </Button>
                <IconButton 
                  size="small" 
                  onClick={refreshEmailVerificationStatus} 
                  title="Refresh status"
                  aria-label="Refresh email verification status"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Container>
        </Box>
      )}

      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: COLORS.background.light,
          py: { xs: 6, md: 8 },
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                fontWeight={700}
                sx={{ 
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  color: COLORS.secondary.main,
                  mb: 3
                }}
              >
                Unique 3D Printed Gifts, Made for You.
              </Typography>
              <Typography
                variant="h6"
                paragraph
                sx={{ 
                  color: COLORS.text.secondary,
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                From personalized photo lamps that glow with your favorite moments to custom keychains and unique home decor, crafted with love and care.
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  size="large"
                  variant="contained"
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontSize: '1.1rem',
                    backgroundColor: COLORS.primary.main,
                    '&:hover': {
                      backgroundColor: COLORS.primary.dark,
                    },
                  }}
                  onClick={() => navigate('/products')}
                >
                  Shop Featured
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  sx={{
                    py: 1.5,
                    px: 4,
                    fontSize: '1.1rem',
                    color: COLORS.primary.main,
                    borderColor: COLORS.primary.main,
                    '&:hover': {
                      borderColor: COLORS.primary.dark,
                      backgroundColor: withOpacity(COLORS.primary.main, 0.04),
                    },
                  }}
                  onClick={() => navigate('/categories')}
                >
                  Browse Categories
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: 400,
                  borderRadius: 2,
                  position: 'relative',
                  overflow: 'hidden',
                  backgroundColor: COLORS.background.dark, // Fallback background
                }}
              >
                <img
                  src="images/Gemini_Generated_Image_zenw06zenw06zenw.png"
                  alt="The Random Store Hero"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '8px',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

  {/* Featured Products Section */}
  <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography
            variant="h4"
            component="h2"
            fontWeight={600}
            color={COLORS.text.primary}
          >
            Featured Products
          </Typography>
          
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            {/* Dynamic label filters */}
            {availableLabels.map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                clickable
                onClick={() => handleLabelToggle(label)}
                sx={{
                  backgroundColor: selectedLabels.includes(label) ? COLORS.primary.main : COLORS.background.light,
                  color: selectedLabels.includes(label) ? 'white' : COLORS.text.secondary,
                  fontWeight: selectedLabels.includes(label) ? 600 : 500,
                  border: selectedLabels.includes(label) ? `1px solid ${COLORS.primary.main}` : `1px solid ${COLORS.border.light}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: selectedLabels.includes(label) ? COLORS.primary.dark : withOpacity(COLORS.primary.main, 0.08),
                    transform: 'scale(1.05)',
                  },
                }}
              />
            ))}
            
            {/* No Label filter - for products without labels */}
            {allFeaturedProducts.some(product => !product.label || !product.label.trim()) && (
              <Chip
                label="No Label"
                size="small"
                clickable
                onClick={() => handleLabelToggle('No Label')}
                sx={{
                  backgroundColor: selectedLabels.includes('No Label') ? COLORS.primary.main : COLORS.background.light,
                  color: selectedLabels.includes('No Label') ? 'white' : COLORS.text.secondary,
                  fontWeight: selectedLabels.includes('No Label') ? 600 : 500,
                  border: selectedLabels.includes('No Label') ? `1px solid ${COLORS.primary.main}` : `1px solid ${COLORS.border.light}`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: selectedLabels.includes('No Label') ? COLORS.primary.dark : withOpacity(COLORS.primary.main, 0.08),
                    transform: 'scale(1.05)',
                  },
                }}
              />
            )}
            
            {/* Clear filters button */}
            {selectedLabels.length > 0 && (
              <Button
                size="small"
                variant="text"
                onClick={clearAllFilters}
                sx={{
                  color: COLORS.text.secondary,
                  textDecoration: 'underline',
                  minWidth: 'auto',
                  px: 1,
                  '&:hover': {
                    backgroundColor: withOpacity(COLORS.primary.main, 0.1),
                  },
                }}
              >
                Clear All
              </Button>
            )}
          </Box>
  </Box>



  <Box sx={{ 
    position: 'relative', 
    width: '100%', 
    mb: 4,
    '& .slick-dots': {
      bottom: -45,
      '& li button:before': {
        fontSize: '12px',
        color: COLORS.primary.main,
        opacity: 0.5,
      },
      '& li.slick-active button:before': {
        opacity: 1,
      },
    },
    '& .slick-prev, & .slick-next': {
      zIndex: 2,
      '&:before': {
        fontSize: '20px',
        color: COLORS.primary.main,
        opacity: 0.8,
      },
      '&:hover:before': {
        opacity: 1,
      },
    },
    '& .slick-prev': {
      left: -25,
    },
    '& .slick-next': {
      right: -25,
    },
  }}>
    {featuredProducts.length > 0 && (
      // Show static grid for 4 or fewer products
      <Grid container spacing={2}>
        {featuredProducts.map((product) => (
          <Grid item xs={12} sm={6} md={3} key={product.docId || product.id}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardMedia
                component="div"
                sx={{
                  height: 200,
                  bgcolor: COLORS.background.light,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onClick={() => navigate(`/products/${product.docId || product.id}`)}
              >
                {product.label && (
                  <Chip
                    label={product.label}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 2,
                      backgroundColor: COLORS.primary.main,
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      height: 20,
                    }}
                  />
                )}
                {product.imageUrls && product.imageUrls.length > 0 ? (
                  <img
                    src={product.imageUrls[0]}
                    alt={product.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      backgroundColor: COLORS.background.dark,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Image sx={{ fontSize: 40, color: COLORS.border.dark }} />
                  </Box>
                )}
              </CardMedia>
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                <Typography
                  variant="subtitle1"
                  component="h3"
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { color: COLORS.primary.main }
                  }}
                  onClick={() => navigate(`/products/${product.docId || product.id}`)}
                >
                  {product.name}
                </Typography>
                <Box sx={{ mb: 1 }}>
                  {product.salePrice && parseFloat(product.salePrice) < parseFloat(product.price) ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          textDecoration: 'line-through', 
                          color: 'text.secondary',
                          fontSize: '0.9rem'
                        }}
                      >
                        ₹{product.price}
                      </Typography>
                      <Typography variant="h6" color="error" fontWeight="bold">
                        ₹{product.salePrice}
                      </Typography>
                      <Chip
                        label={`${Math.round((1 - parseFloat(product.salePrice) / parseFloat(product.price)) * 100)}% OFF`}
                        size="small"
                        color="error"
                        variant="filled"
                        sx={{ 
                          height: 20,
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      ₹{product.price}
                    </Typography>
                  )}
                </Box>
              </CardContent>
              <CardActions sx={{ p: 2, pt: 0 }}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: COLORS.primary.main,
                    '&:hover': {
                      backgroundColor: COLORS.primary.dark,
                    },
                  }}
                  onClick={() => handleAddToCart(product)}
                >
                  Add to Cart
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
    )}
  </Box>

        {/* No featured products message */}
        {featuredProducts.length === 0 && !loading && (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="text.secondary" mb={2}>
              No featured products available
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Check back later for our latest featured items, or browse all products below.
            </Typography>
            <Button 
              variant="contained"
              onClick={() => navigate('/products')}
              sx={{ 
                backgroundColor: COLORS.primary.main,
                '&:hover': { backgroundColor: COLORS.primary.dark }
              }}
            >
              Browse All Products
            </Button>
          </Box>
        )}
      </Container>

      {/* Shop by Category Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          fontWeight={600}
          color={COLORS.text.primary}
          mb={4}
        >
          Shop by Category
        </Typography>
        <Grid container spacing={2}>
          {categories.map((category) => (
            <Grid item xs={6} sm={4} md={2} key={category.id}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: COLORS.background.light,
                  border: `1px solid ${withOpacity(COLORS.primary.main, 0.12)}`,
                  '&:hover': {
                    backgroundColor: COLORS.primary.main,
                    color: 'white',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${withOpacity(COLORS.primary.main, 0.3)}`,
                  },
                }}
                onClick={() => navigate(`/products?category=${category.id}`)}
              >
                <Typography
                  variant="body1"
                  fontWeight={500}
                  sx={{
                    color: 'inherit',
                  }}
                >
                  {category.name}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        {categories.length === 0 && !loading && (
          <Box textAlign="center" py={4}>
            <Typography variant="h6" color="text.secondary">
              Categories will appear here once configured.
            </Typography>
          </Box>
        )}
      </Container>
      </Box>
    </>
  );
};

export default Home;
